import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../../hooks/useInView'
import { RouteTransitionContext } from '../../components/AnimatedRoutes'

/* ── UI animation variants ──────────────────────── */
const staggerUI = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.38, 0.005, 0.215, 1] as const } },
}

/* ── Feature data for alternating layout ────────── */
const features = [
  {
    title: 'Marketplaces',
    desc: 'A marketplace run by your community — where members buy from people they trust.',
    bullets: [
      { label: 'Built on conversation', text: 'Every listing carries the chatter around it, so members know what they’re buying and from whom.' },
      { label: 'Always current', text: 'New drops and deals show up everywhere the moment you post them.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-oak-black">Trending in your community</span>
          <span className="text-[11px] text-oak-green">Live</span>
        </motion.div>
        {[
          { name: 'Trail Ridge Daypack', meta: '24 talking about it', price: '$89.00' },
          { name: 'Basecamp Lantern', meta: 'Featured by 3 creators', price: '$42.00' },
          { name: 'Ridgeline Hammock', meta: 'New drop · today', price: '$45.00' },
        ].map((p, i) => (
          <motion.div key={i} variants={fadeUp} className="flex items-center justify-between rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-3.5 py-3">
            <div>
              <div className="text-[13px] font-medium text-oak-black">{p.name}</div>
              <div className="text-[11px] text-oak-black/40">{p.meta}</div>
            </div>
            <span className="text-[14px] font-semibold text-oak-black">{p.price}</span>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Storefronts',
    desc: 'Your own storefront — your name, your look, your domain.',
    bullets: [
      { label: 'Your brand, not ours', text: 'No Oak branding. Your logo, colors, and identity front and center.' },
      { label: 'Your own domain', text: 'Run the storefront on a domain your community already knows.' },
      { label: 'Make it yours', text: 'Shape the layout, theme, and content to match the way your community looks.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between rounded-t-xl bg-oak-black/[0.04] px-3.5 py-2">
          <span className="text-[11px] font-medium text-oak-black/40">summitco.shop</span>
          <span className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-oak-black/15" />
            <span className="h-2 w-2 rounded-full bg-oak-black/15" />
            <span className="h-2 w-2 rounded-full bg-oak-black/15" />
          </span>
        </motion.div>
        <motion.div variants={fadeUp} className="flex items-center justify-between border-b border-oak-black/[0.06] px-3.5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-oak-green/10 text-[12px]">🏔</div>
            <span className="text-[14px] font-bold tracking-[-0.2px] text-oak-black">Summit&nbsp;Co.</span>
          </div>
          <span className="text-[11px] font-medium text-oak-black/40">Shop · About · Cart</span>
        </motion.div>
        <motion.div variants={fadeUp} className="grid grid-cols-2 gap-2.5 pt-1">
          {[
            { name: 'Trail Ridge Daypack', price: '$89.00' },
            { name: 'Basecamp Lantern', price: '$42.00' },
          ].map((p, i) => (
            <div key={i} className="rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] p-3">
              <div className="mb-2 h-12 rounded-lg bg-oak-black/[0.05]" />
              <div className="text-[12px] font-medium text-oak-black">{p.name}</div>
              <div className="text-[12px] font-semibold text-oak-black">{p.price}</div>
            </div>
          ))}
        </motion.div>
      </>
    ),
  },
  {
    title: 'Integrated Payments',
    desc: 'Stripe checkout built into every community. No payment headaches.',
    bullets: [
      { label: 'One-tap checkout', text: 'Members buy in a tap with saved payment methods.' },
      { label: 'Built-in shipping', text: 'Shippo handles labels and tracking so sellers don’t have to.' },
      { label: 'Seller payouts', text: 'Stripe Connect sends sellers their share the moment a sale clears.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between">
          <span className="text-[15px] font-semibold text-oak-black">Trail Ridge Daypack</span>
          <span className="text-[15px] font-semibold text-oak-black">$89.00</span>
        </motion.div>
        <motion.div variants={fadeUp} className="h-px bg-oak-black/[0.06]" />
        <motion.div variants={fadeUp} className="text-[13px] text-oak-black/50">1× Trail Ridge Daypack — $89.00</motion.div>
        <motion.div variants={fadeUp} className="text-[13px] text-oak-black/50">Shipping (Shippo) — $5.95</motion.div>
        <motion.div variants={fadeUp} className="h-px bg-oak-black/[0.06]" />
        <motion.div variants={fadeUp} className="flex items-center justify-between">
          <span className="text-[14px] font-semibold text-oak-black">Total</span>
          <span className="text-[16px] font-bold text-oak-black">$94.95</span>
        </motion.div>
        <motion.div variants={fadeUp} className="rounded-xl bg-oak-black py-3 text-center text-[14px] font-medium text-white">Pay now</motion.div>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 pt-1">
          <span className="text-[11px] text-oak-black/30">Powered by</span>
          <span className="text-[12px] font-semibold text-oak-black/40">Stripe</span>
        </motion.div>
      </>
    ),
  },
  {
    title: 'Member Payouts',
    desc: 'Instant payouts for anyone who sells, powered by Stripe Connect.',
    bullets: [
      { label: 'Real-time splits', text: 'Every sale is split and sent to the right person the moment it clears.' },
      { label: 'Self-serve withdrawals', text: 'Members move money to their bank account without leaving the community.' },
      { label: 'Clear history', text: 'Every sale, fee, and transfer in one tidy list, so nobody has to wonder.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-1 flex items-baseline justify-between">
          <span className="text-[12px] font-medium uppercase tracking-wider text-oak-black/40">Balance</span>
          <span className="text-[20px] font-bold text-oak-black">$412.35</span>
        </motion.div>
        {[
          { label: 'Trail Ridge Daypack sale', amount: '+$84.55', time: '2 min ago', isWithdraw: false },
          { label: 'Campfire Mug Set sale', amount: '+$22.80', time: '18 min ago', isWithdraw: false },
          { label: 'Basecamp Lantern sale', amount: '+$42.00', time: '3 hr ago', isWithdraw: false },
          { label: 'Payout to bank ••4821', amount: '-$340.00', time: '1 day ago', isWithdraw: true },
        ].map((e, i) => (
          <motion.div key={i} variants={fadeUp} className="flex items-center justify-between rounded-lg bg-oak-black/[0.03] px-3 py-2.5">
            <div>
              <div className="text-[13px] font-medium text-oak-black">{e.label}</div>
              <div className="text-[11px] text-oak-black/40">{e.time}</div>
            </div>
            <span className={`text-[14px] font-semibold ${e.isWithdraw ? 'text-oak-black/40' : 'text-oak-green'}`}>
              {e.amount}
            </span>
          </motion.div>
        ))}
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 pt-1">
          <span className="text-[11px] text-oak-black/30">Powered by</span>
          <span className="text-[12px] font-semibold text-oak-black/40">Stripe Connect</span>
        </motion.div>
      </>
    ),
  },
  {
    title: 'Affiliate Tools',
    desc: 'Give members, creators, and partners their own affiliate links. Bring people in, get credit.',
    bullets: [
      { label: 'Referral links', text: 'Every product and group gets an affiliate link anyone can share.' },
      { label: 'Their own dashboard', text: 'Each person sees their clicks, sales, and earnings in one place.' },
      { label: 'Automatic payouts', text: 'Commissions go out through Stripe Connect without you lifting a finger.' },
      { label: 'Works for everyone', text: 'Members, creators, influencers, and partner communities can all earn.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-oak-black">Affiliates · this month</span>
          <span className="text-[11px] text-oak-green">3 active</span>
        </motion.div>
        <motion.div variants={fadeUp} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-oak-black/30">
          <span>Member</span><span>Clicks</span><span>Sales</span><span className="text-right">Earned</span>
        </motion.div>
        {[
          { avatar: 'JM', name: 'Jake M.', clicks: '412', sales: '18', earned: '$126.40' },
          { avatar: 'PS', name: 'Priya S.', clicks: '287', sales: '11', earned: '$78.20' },
          { avatar: 'DK', name: 'Dana K.', clicks: '164', sales: '6', earned: '$41.50' },
        ].map((a, i) => (
          <motion.div key={i} variants={fadeUp} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-lg bg-oak-black/[0.03] px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-200 text-[10px] font-bold text-gold-700">{a.avatar}</div>
              <span className="text-[13px] font-medium text-oak-black">{a.name}</span>
            </div>
            <span className="text-[12px] text-oak-black/40">{a.clicks}</span>
            <span className="text-[12px] text-oak-black/40">{a.sales}</span>
            <span className="text-right text-[13px] font-semibold text-oak-green">{a.earned}</span>
          </motion.div>
        ))}
        <motion.div variants={fadeUp} className="flex items-center justify-between rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-3.5 py-2.5">
          <div>
            <div className="text-[11px] text-oak-black/40">Your affiliate link</div>
            <div className="text-[12px] font-medium text-oak-black">summitco.shop/r/jakem</div>
          </div>
          <span className="rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-oak-black shadow-sm">Copy</span>
        </motion.div>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 pt-1">
          <span className="text-[11px] text-oak-black/30">Payouts by</span>
          <span className="text-[12px] font-semibold text-oak-black/40">Stripe Connect</span>
        </motion.div>
      </>
    ),
  },
  {
    title: 'Enriched Products',
    desc: 'Every product carries real context — not just a picture and a price.',
    bullets: [
      { label: 'Rich context', text: 'Oak surrounds each product with who’s talking about it and why they like it.' },
      { label: 'Always up to date', text: 'The same details everywhere your products appear, so nothing goes stale.' },
      { label: 'No busywork', text: 'Deals and new drops go out on their own. Nothing to update by hand.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between">
          <span className="text-[15px] font-semibold text-oak-black">Trail Ridge Daypack</span>
          <span className="text-[15px] font-semibold text-oak-black">$89.00</span>
        </motion.div>
        <motion.div variants={fadeUp} className="text-[12px] text-oak-black/40">Enriched by Oak · updated 2 min ago</motion.div>
        <motion.div variants={fadeUp} className="flex flex-wrap gap-1.5 pt-1">
          {['Trending', 'Best for thru-hikes', '3 creators featured', 'In stock'].map((t, i) => (
            <span key={i} className="rounded-full bg-oak-green/10 px-2.5 py-0.5 text-[11px] font-medium text-oak-green">{t}</span>
          ))}
        </motion.div>
        <motion.div variants={fadeUp} className="h-px bg-oak-black/[0.06]" />
        {[
          { source: '@trailrunner', note: '“Held 30mph winds at Glacier Point.”' },
          { source: 'YouTube · GearLab', note: '12 min review · 84k views' },
          { source: 'r/Ultralight', note: 'Mentioned in 6 threads this week' },
        ].map((c, i) => (
          <motion.div key={i} variants={fadeUp} className="flex items-center justify-between rounded-lg bg-oak-black/[0.03] px-3 py-2">
            <span className="text-[12px] font-medium text-oak-black/70">{c.source}</span>
            <span className="text-[11px] text-oak-black/40">{c.note}</span>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Direct Messaging',
    desc: 'Real conversations between real members.',
    bullets: [
      { label: 'In-app messaging', text: 'Ask a question or haggle a little without leaving the community.' },
      { label: 'Tied to listings', text: 'Messages link straight to the listing or purchase they’re about.' },
      { label: 'Trust signals', text: 'See a member’s reputation and history right in the conversation.' },
    ],
    ui: (
      <>
        {[
          { text: 'Hey, is the hammock still available?', own: false, time: '2:14 PM' },
          { text: 'Yes! Just used it once. Comes with the straps too.', own: true, time: '2:16 PM' },
          { text: 'Amazing. Can you do $35?', own: false, time: '2:17 PM' },
          { text: "Meet at $38 and it's yours", own: true, time: '2:18 PM' },
          { text: 'Deal. Sending payment now.', own: false, time: '2:19 PM' },
        ].map((m, i) => (
          <motion.div key={i} variants={fadeUp} className={`flex ${m.own ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${m.own ? 'bg-oak-black text-white' : 'bg-oak-black/[0.05] text-oak-black'}`}>
              <p className="text-[13px] leading-[1.4]">{m.text}</p>
              <p className={`mt-0.5 text-[10px] ${m.own ? 'text-white/40' : 'text-oak-black/30'}`}>{m.time}</p>
            </div>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Public & Private Groups',
    desc: 'Choose who gets in — open to anyone, or members-only.',
    bullets: [
      { label: 'Public groups', text: 'Open spaces anyone can discover, browse, and join right away.' },
      { label: 'Private groups', text: 'Invite-only or approval-based access for inner circles and close-knit groups.' },
      { label: 'Flexible visibility', text: 'Decide what non-members can see — tease a few products or keep it all behind the door.' },
    ],
    ui: (
      <>
        {/* Community cards */}
        {[
          { name: 'Rocky Mountain Gear Co-op', visibility: 'Public', members: '2.4k', icon: '🏔', tagline: 'Open to all — browse and join instantly', isPublic: true },
          { name: 'Summit Guides Collective', visibility: 'Private', members: '86', icon: '🔒', tagline: 'Invite only — approved members', isPublic: false },
        ].map((c, i) => (
          <motion.div key={i} variants={fadeUp} className="rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-4 py-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-[18px]">{c.icon}</span>
                <div>
                  <span className="text-[13px] font-semibold text-oak-black">{c.name}</span>
                  <p className="text-[11px] text-oak-black/40">{c.tagline}</p>
                </div>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${c.isPublic ? 'bg-oak-green/10 text-oak-green' : 'bg-oak-black/[0.06] text-oak-black/50'}`}>
                {c.visibility}
              </span>
            </div>
            <div className="mt-2.5 flex items-center gap-3">
              <span className="text-[11px] text-oak-black/30">{c.members} members</span>
              <span className="text-[11px] text-oak-black/30">·</span>
              <span className={`text-[11px] font-medium ${c.isPublic ? 'text-oak-green' : 'text-oak-black/40'}`}>
                {c.isPublic ? 'Join now →' : 'Request access →'}
              </span>
            </div>
          </motion.div>
        ))}
        {/* Visibility toggle mockup */}
        <motion.div variants={fadeUp} className="rounded-xl bg-oak-black/[0.03] px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[12px] font-semibold text-oak-black">Group Visibility</p>
              <p className="text-[11px] text-oak-black/35">Control who can see your listings</p>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-oak-black/[0.06] p-0.5">
              <span className="rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-oak-black shadow-sm">Public</span>
              <span className="px-2.5 py-1 text-[11px] font-medium text-oak-black/30">Private</span>
            </div>
          </div>
        </motion.div>
      </>
    ),
  },
  {
    title: 'Threaded Comments',
    desc: 'Every listing becomes a conversation — questions, reviews, and replies, inline.',
    bullets: [
      { label: 'Ask right there', text: 'Members ask about sizing, condition, and fit right on the listing.' },
      { label: 'Nested replies', text: 'Sellers and members keep threads tidy with replies that stay in context.' },
      { label: 'Honest feedback', text: 'Real words from real members, so people know what they’re getting.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-3 text-[13px] font-medium text-oak-black/40">Alpine 2P Tent</motion.div>
        {[
          { user: 'Jake M.', text: 'Held up great in 30mph winds at Glacier Point last weekend. Solid tent.', time: '3h ago', avatar: 'JM', isReply: false },
          { user: 'Priya S.', text: "How's the vestibule space? Need room for muddy boots.", time: '1h ago', avatar: 'PS', isReply: false },
          { user: 'Jake M.', text: 'Fits boots + a pack easily. The side entry helps a lot.', time: '45m ago', avatar: 'JM', isReply: true },
          { user: 'Priya S.', text: 'Perfect, ordering one now. Thanks for the detail.', time: '22m ago', avatar: 'PS', isReply: true },
        ].map((c, i) => (
          <motion.div key={i} variants={fadeUp} className={c.isReply ? 'ml-8' : ''}>
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-200 text-[10px] font-bold text-gold-700">
                {c.avatar}
              </div>
              <span className="text-[13px] font-semibold text-oak-black">{c.user}</span>
              <span className="text-[11px] text-oak-black/30">{c.time}</span>
            </div>
            <p className="mt-1 ml-8 text-[13px] leading-[1.4] text-oak-black/70">{c.text}</p>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Niche Forums',
    desc: 'Dedicated spaces for every topic, game, and shared interest.',
    bullets: [
      { label: 'Topic channels', text: 'Make focused spaces around the things your community loves.' },
      { label: 'Threaded discussion', text: 'Members share, ask, and answer — and the good stuff sticks around.' },
      { label: 'Community moderation', text: 'Trusted members help keep conversations friendly and on topic.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-3 flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-oak-green" />
          <span className="text-[13px] font-semibold text-oak-black">YETI Enthusiasts</span>
          <span className="text-[11px] text-oak-black/30">128 members</span>
        </motion.div>
        {[
          { avatar: 'MT', user: 'Marcus T.', text: 'Just got the new Rambler 26oz in alpine yellow. This color is incredible.', likes: 24 },
          { avatar: 'DK', user: 'Dana K.', text: "Anyone compared the Hopper Flip 12 vs 18 for day hikes? Can't decide.", likes: 8 },
          { avatar: 'CL', user: 'Chris L.', text: 'The Flip 12 fits perfectly in the Trail Ridge Daypack. 18 is better for car camping.', likes: 15 },
        ].map((p, i) => (
          <motion.div key={i} variants={fadeUp} className="rounded-xl bg-oak-black/[0.03] px-3.5 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-200 text-[10px] font-bold text-gold-700">{p.avatar}</div>
              <span className="text-[13px] font-semibold text-oak-black">{p.user}</span>
            </div>
            <p className="mt-1.5 text-[13px] leading-[1.4] text-oak-black/70">{p.text}</p>
            <div className="mt-2 text-[11px] text-oak-black/30">♥ {p.likes}</div>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Geolocation',
    desc: 'See what’s nearby — local sellers, drops, and meetups for your community.',
    bullets: [
      { label: 'Local discovery', text: 'Members find products and sellers close by, ready to meet up or ship.' },
      { label: 'Local drops', text: 'Release something just for the places your community is most active.' },
      { label: 'Map view', text: 'Browse listings and groups on a map built right into your community.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-oak-black">Near you · Denver, CO</span>
          <span className="text-[11px] text-oak-green">12 nearby</span>
        </motion.div>
        <motion.div variants={fadeUp} className="relative h-28 overflow-hidden rounded-xl bg-oak-black/[0.04]">
          <div className="absolute inset-0 opacity-50 [background-image:linear-gradient(to_right,rgba(0,0,0,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.06)_1px,transparent_1px)] [background-size:20px_20px]" />
          {[
            { top: '22%', left: '30%' },
            { top: '55%', left: '62%' },
            { top: '40%', left: '48%' },
          ].map((pin, i) => (
            <div key={i} className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-oak-green ring-4 ring-oak-green/20" style={{ top: pin.top, left: pin.left }} />
          ))}
        </motion.div>
        {[
          { name: 'Basecamp Lantern', dist: '0.8 mi away' },
          { name: 'Ridgeline Hammock', dist: '1.4 mi away' },
        ].map((p, i) => (
          <motion.div key={i} variants={fadeUp} className="flex items-center justify-between rounded-lg bg-oak-black/[0.03] px-3 py-2">
            <span className="text-[13px] font-medium text-oak-black">{p.name}</span>
            <span className="text-[11px] text-oak-black/40">{p.dist}</span>
          </motion.div>
        ))}
      </>
    ),
  },
]

function AlternatingFeature({ feature, index }: { feature: (typeof features)[number]; index: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { threshold: 0.15 })
  const isTransitioning = useContext(RouteTransitionContext)
  const isReversed = index % 2 === 1
  const show = !isTransitioning && isInView

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const }}
      className={`flex flex-col items-center gap-10 md:gap-16 ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'}`}
    >
      {/* Text side */}
      <div className="flex-1">
        <h3 className="text-[clamp(28px,4vw,40px)] font-medium leading-[1.1] tracking-[-0.8px] text-oak-black">
          {feature.title}
        </h3>
        <p className="mt-3 max-w-[400px] text-[clamp(15px,1.5vw,18px)] leading-[1.5] tracking-[-0.2px] text-oak-black/50">
          {feature.desc}
        </p>
        <div className="mt-6 space-y-4">
          {feature.bullets.map((b, i) => (
            <div key={i}>
              <p className="text-[15px] font-semibold tracking-[-0.2px] text-oak-black">{b.label}</p>
              <p className="mt-0.5 text-[14px] leading-[1.5] text-oak-black/45">{b.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* UI side */}
      <div className="w-full flex-1">
        <motion.div
          className="space-y-2.5 rounded-2xl border border-oak-black/[0.06] bg-white px-6 py-6 md:px-8"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}
          variants={staggerUI}
          initial="hidden"
          animate={show ? 'visible' : 'hidden'}
        >
          {feature.ui}
        </motion.div>
      </div>
    </motion.div>
  )
}

export default function FeaturesAlternating() {
  return (
    <section className="bg-oak-cream pb-32 pt-8 md:px-16 md:pb-44 md:pt-12">
      <div className="mx-auto max-w-nav px-6 space-y-20 md:space-y-32">
        {features.map((f, i) => (
          <AlternatingFeature key={f.title} feature={f} index={i} />
        ))}
      </div>
    </section>
  )
}
